import React, { useEffect, useState } from 'react';
import { Modal } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import './SubscriptionCancellationModal.css';

export default function SubscriptionCancellationModal({
  open,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);

  const cancellationReasons = [
    t('Trop cher par rapport à la valeur'),
    t("Je n’utilise pas assez l’outil"),
    t('Fonctionnalités insuffisantes'),
    t('Difficulté d’utilisation / UX'),
    t('Problèmes techniques / bugs'),
    t('J’ai trouvé une alternative'),
    t('Autre'),
  ];

  useEffect(() => {
    if (!open) {
      setSelectedReason('');
      setComment('');
      setError('');
      setIsSubmitting(false);
      setIsSuccessVisible(false);
    }
  }, [open]);

  const closeModal = () => {
    if (isSubmitting) return;
    setSelectedReason('');
    setComment('');
    setError('');
    setIsSuccessVisible(false);
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!selectedReason) {
      setError(t('Veuillez sélectionner un motif de résiliation.'));
      return;
    }

    setError('');

    if (typeof onConfirm !== 'function') {
      closeModal();
      return;
    }

    try {
      setIsSubmitting(true);
      await onConfirm({
        reason: selectedReason,
        comment: comment.trim(),
      });
      setIsSuccessVisible(true);
    } catch (submitError) {
      setError(t('Impossible de traiter la résiliation pour le moment.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    isSuccessVisible ? (
      <QuitProjectCreationModal
        isOpen={open}
        onClose={closeModal}
        title={t('Résiliation confirmée')}
        message={t('Votre résiliation a bien été prise en compte.')}
        suggestion={t('Nous sommes désolés de vous voir partir et espérons vous revoir bientôt ❤️')}
        primaryButtonLabel={t('Fermer')}
        onPrimaryAction={closeModal}
        showSecondaryButton={false}
      />
    ) : (
      <Modal
        isOpen={open}
        toggle={closeModal}
        className="subscription-cancellation-modal"
        backdrop={true}
        keyboard={!isSubmitting}
        centered
      >
        <div className="subscription-cancellation-modal-content">
          <div className="subscription-cancellation-modal-header">
            <div className="subscription-cancellation-modal-title-wrapper">
              <h2
                id="subscription-cancellation-title"
                className="subscription-cancellation-modal-title"
              >
                {t('Résilier mon abonnement')}
              </h2>
            </div>

            <button
              type="button"
              className="subscription-cancellation-modal-close-button"
              onClick={closeModal}
              aria-label={t('Fermer')}
              disabled={isSubmitting}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12M4 4L12 12" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="subscription-cancellation-modal-body">
            <p className="subscription-cancellation-modal-message">
              {t('Avant de partir, peux-tu nous dire pourquoi tu résilies ?')}
            </p>

            <div className="subscription-cancellation-modal-suggestion-box">
              <p className="subscription-cancellation-modal-suggestion-text">
                {t('Votre abonnement sera arrêté à la fin de la période en cours.')}
              </p>
            </div>

            <div className="subscription-cancellation-modal-reasons">
              {cancellationReasons.map((reason) => (
                <label
                  key={reason}
                  className={`subscription-cancellation-modal-option ${
                    selectedReason === reason ? 'subscription-cancellation-modal-option--selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="subscription-cancellation-reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                  />
                  <span>{reason}</span>
                </label>
              ))}
            </div>

            <div className="subscription-cancellation-modal-field">
              <label
                className="subscription-cancellation-modal-field-label"
                htmlFor="subscription-cancellation-comment"
              >
                {t('Commentaire')}
              </label>
              <textarea
                id="subscription-cancellation-comment"
                className="subscription-cancellation-modal-textarea"
                rows="4"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('Ajoutez un commentaire (optionnel)')}
              />
            </div>

            {error && (
              <div className="subscription-cancellation-modal-error">
                {error}
              </div>
            )}
          </div>

          <div className="subscription-cancellation-modal-footer">
            <button
              type="button"
              className="subscription-cancellation-modal-cancel-button"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              {t('Annuler')}
            </button>
            <button
              type="button"
              className="subscription-cancellation-modal-confirm-button"
              onClick={handleConfirm}
              disabled={isSubmitting || !selectedReason}
            >
              {isSubmitting ? t('Confirmation...') : t('Confirmer la résiliation')}
            </button>
          </div>
        </div>
      </Modal>
    )
  );
}
